import React from 'react';
import {
  Box,
  Typography,
  Button,
  Sheet,
  Stack,
  Divider
} from '@mui/joy';
import { useNavigate } from 'react-router-dom';

const PricingPage: React.FC = () => {
  const navigate = useNavigate();

  const handleContinue = () => {
    // Navigate to the payment page
    navigate('/hiring/payment');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f4f4f4',
        padding: '20px',
      }}
    >
      <Sheet
        sx={{
          width: '100%',
          maxWidth: '600px',
          padding: '40px',
          borderRadius: '16px',
          backgroundColor: '#ffffff',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Stack spacing={4} alignItems="center">
          {/* Header */}
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Typography 
              level="h1" 
              fontSize="32px" 
              sx={{ 
                fontWeight: 600, 
                color: '#1A1B1D',
                marginBottom: '8px'
              }}
            >
              Our Pricing
            </Typography>
            <Typography 
              level="description"
              sx={{ 
                fontSize: '18px',
                color: '#7B7F85',
                lineHeight: 1.5,
                maxWidth: '500px'
              }}
            >
              GEEK is a staffing agency. We only charge you when we successfully place a candidate who stays.
            </Typography>
          </Stack>

          <Divider sx={{ width: '100%', borderColor: '#E5E7EB' }} />

          {/* Pricing Models */}
          <Stack spacing={3} width="100%">
            {/* Hourly Staff */}
            <Box
              sx={{
                padding: '24px',
                borderRadius: '12px',
                backgroundColor: '#F8F9FA',
                border: '2px solid #E5E7EB',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: '#2e2d7e',
                  backgroundColor: '#F0F2F5',
                }
              }}
            >
              <Stack spacing={2}>
                <Typography 
                  level="h3"
                  sx={{ 
                    fontSize: '24px',
                    fontWeight: 600,
                    color: '#1A1B1D'
                  }}
                >
                  Hourly Staff
                </Typography>
                <Typography 
                  level="body-lg"
                  sx={{ 
                    fontSize: '20px',
                    fontWeight: 500,
                    color: '#2e2d7e'
                  }}
                >
                  15% of 1 month's pay
                </Typography>
                <Typography 
                  level="body-sm"
                  sx={{ 
                    fontSize: '16px',
                    color: '#7B7F85',
                    lineHeight: 1.4
                  }}
                >
                  Perfect for temporary positions, seasonal work, or project-based staffing needs.
                </Typography>
              </Stack>
            </Box>

            {/* Salary Staff */}
            <Box
              sx={{
                padding: '24px',
                borderRadius: '12px',
                backgroundColor: '#F8F9FA',
                border: '2px solid #E5E7EB',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: '#2e2d7e',
                  backgroundColor: '#F0F2F5',
                }
              }}
            >
              <Stack spacing={2}>
                <Typography 
                  level="h3"
                  sx={{ 
                    fontSize: '24px',
                    fontWeight: 600,
                    color: '#1A1B1D'
                  }}
                >
                  Salary Staff
                </Typography>
                <Typography 
                  level="body-lg"
                  sx={{ 
                    fontSize: '20px',
                    fontWeight: 500,
                    color: '#2e2d7e'
                  }}
                >
                  10% of 1 year's pay
                </Typography>
                <Typography 
                  level="body-sm"
                  sx={{ 
                    fontSize: '16px',
                    color: '#7B7F85',
                    lineHeight: 1.4
                  }}
                >
                  Ideal for permanent positions, full-time roles, and long-term staffing solutions.
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Divider sx={{ width: '100%', borderColor: '#E5E7EB' }} />

          {/* Continue Button */}
          <Box width="100%">
            <Button
              variant="solid"
              fullWidth
              onClick={handleContinue}
              sx={{
                height: '56px',
                fontSize: '18px',
                fontWeight: 600,
                backgroundColor: '#2e2d7e',
                '&:hover': {
                  backgroundColor: '#23225f',
                },
              }}
            >
              Continue
            </Button>
          </Box>

          {/* Additional Info */}
          <Typography 
            level="body-sm" 
            sx={{ 
              color: '#7B7F85', 
              fontSize: '14px',
              fontWeight: 400,
              textAlign: 'center',
              maxWidth: '500px'
            }}
          >
            No upfront fees. No hidden costs. You only pay when we successfully place a qualified candidate who meets your requirements.
          </Typography>
        </Stack>
      </Sheet>
    </Box>
  );
};

export default PricingPage; 